import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import Selector from 'components/Form/Selector';
import Modal from '../../Presentation/Modal';
import { setUserParam } from '../../../actions';
import withTranslation from '../../../hoc/withTranslation';
import {
  StyledContrastPanel,
  StyledContrastPanelColumnsContainer,
  StyledContrastPanelFooterSettings,
  StyledContrastPanelHeader,
} from '../../Wizard/Global/WizardStyledContrastPanel';
import { StyledText } from '../../Wizard/Global/WizardStyled';
import Section from '../../Form/Section';
import { StyledAccessContainer } from '../StyledComponents';
import mapSettings from '../../../global/img/map-settings.svg';
import { PANELS, defaultMapSettings } from '../../../reducers/user';
import Button from '../../Form/Button';
import MultipleActionSetting from './MultipleActionSetting';
import SingleActionSetting from './SingleActionSetting';

export const OPTIONS = { General: 'general', Solution: 'solution' };

export const MAP_SETTINGS = {
  [OPTIONS.General]: {
    SHOW_JOB_CLUSTERS: 'showJobClusters',
    SATELLITE_VIEW: 'satelliteView',
  },
  [OPTIONS.Solution]: {
    SHOW_STOPS: 'showStops',
    SHOW_CLUSTERS: 'showStopClusters',
    GRADIENT_ROUTES: 'gradientRoutes',
    SHOW_STRAIGHT_ROUTE_LINES: 'showStraightRouteLines',
    SHOW_TOUR_DIRECTION_ANIMATION: 'showTourDirectionAnimation',
    SHOW_TOUR_CONVEX_HULL: 'showTourConvexHull',
    SHOW_TOUR_CONCAVE_HULL: 'showTourConcaveHull',
    HIDE_ALL_ROUTES: 'hideAllRoutes',
    SHOW_INTERSECTIONS: 'showIntersections',
    SHOW_OVERLAPS: 'showOverlaps',
    HIDE_VISITED: 'hideVisited',
    SHOW_ONLY_FIRST_VISITS: 'showOnlyFirstVisits',
  },
};

export const StyledScrollableContent = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
`;

const MapSettingsPanel = ({
  user,
  translations: { mapSettingsPanel, solverConfigLabels },
  wizardShown,
}) => {
  const dispatch = useDispatch();
  const {
    display: { showProblem },
  } = useSelector(({ mapContainer }) => mapContainer);
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);

  const scrollableContentRef = useRef(null);

  const [showPanel, setShowPanel] = useState(false);
  const [selectedType, setSelectedType] = useState(OPTIONS.General);

  const settings = Object.entries(MAP_SETTINGS[selectedType]).map(([key, value]) => ({
    key,
    value,
  }));
  const togglePanel = useCallback(() => {
    handleSetUserParam({ openPanel: showPanel ? undefined : PANELS.MAP_SETTINGS });
    setShowPanel(!showPanel);
  }, [showPanel, handleSetUserParam]);

  const handleDoneClick = () => {
    setTimeout(() => {
      togglePanel();
    }, 100);
  };

  const handleOnSwitchChange = useCallback(
    (id, value) => {
      const isConcave = id === MAP_SETTINGS[OPTIONS.Solution].SHOW_TOUR_CONCAVE_HULL;
      const showStopsOff = id === MAP_SETTINGS[OPTIONS.Solution].SHOW_STOPS && !value;
      const showConvexHull = id === MAP_SETTINGS[OPTIONS.Solution].SHOW_TOUR_CONVEX_HULL && value;
      const showConcaveHull = isConcave && value;

      let payload;

      if (isConcave) {
        payload = {
          ...user.mapSettings[id],
          enabled: value,
          userChange: true,
        };
      } else {
        payload = value;
      }

      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [id]: payload,
          ...(showStopsOff && { [MAP_SETTINGS[OPTIONS.Solution].SHOW_CLUSTERS]: false }),
          ...(showConvexHull && {
            [MAP_SETTINGS[OPTIONS.Solution].SHOW_TOUR_CONCAVE_HULL]: false,
          }),
          ...(showConcaveHull && {
            [MAP_SETTINGS[OPTIONS.Solution].SHOW_TOUR_CONVEX_HULL]: false,
          }),
        },
      });
    },
    [handleSetUserParam, user.mapSettings],
  );

  const handleOnChangeInput = useCallback(
    (id, value) => {
      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [id]: { ...user.mapSettings[id], value, userChange: true, enabled: true },
        },
      });
    },
    [user.mapSettings, handleSetUserParam],
  );

  const onResetDefault = useCallback(() => {
    if (selectedType === OPTIONS.General) {
      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [MAP_SETTINGS.general.SHOW_JOB_CLUSTERS]: true,
          [MAP_SETTINGS.general.SATELLITE_VIEW]: false,
        },
      });
      return;
    }

    const resetSettings = Object.keys(MAP_SETTINGS[selectedType]).map(
      (k) => MAP_SETTINGS[selectedType][k],
    );
    const filteredSettings = Object.keys(defaultMapSettings)
      .filter((setting) => resetSettings.includes(setting))
      .reduce((obj, key) => {
        if (selectedType === OPTIONS.Solution) {
          obj[key] = { ...defaultMapSettings[key] };
        } else {
          obj[key] = {
            ...defaultMapSettings[key],
            ...(user.mapSettings[key]?.value !== undefined
              ? { value: user.mapSettings[key].value }
              : {}),
            ...(user.mapSettings[key]?.enabled !== undefined
              ? { enabled: user.mapSettings[key].enabled }
              : {}),
          };
        }
        return obj;
      }, {});

    if (selectedType === OPTIONS.General) {
      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          ...filteredSettings,
          [MAP_SETTINGS.general.SHOW_JOB_CLUSTERS]: true,
        },
      });
      return;
    }

    if (selectedType === OPTIONS.Solution) {
      Object.keys(filteredSettings).forEach((key) => {
        if (
          filteredSettings[key] &&
          typeof filteredSettings[key] === 'object' &&
          Object.keys(filteredSettings[key]).length === 0
        ) {
          filteredSettings[key] = false;
        }
      });
      filteredSettings[MAP_SETTINGS.solution.SHOW_STOPS] = true;
      filteredSettings[MAP_SETTINGS.solution.SHOW_CLUSTERS] = true;

      filteredSettings[MAP_SETTINGS.general.SATELLITE_VIEW] =
        user.mapSettings[MAP_SETTINGS.general.SATELLITE_VIEW];
    }

    if (selectedType === OPTIONS.General) {
      filteredSettings[MAP_SETTINGS.general.SATELLITE_VIEW] = false;
    }

    if (
      selectedType === OPTIONS.General &&
      Object.prototype.hasOwnProperty.call(
        filteredSettings,
        MAP_SETTINGS[OPTIONS.General].CONVERT_TO_TERRITORY,
      )
    ) {
      filteredSettings[MAP_SETTINGS[OPTIONS.General].CONVERT_TO_TERRITORY] = null;
    }

    handleSetUserParam({
      mapSettings:
        selectedType === OPTIONS.Solution || selectedType === OPTIONS.General
          ? { ...user.mapSettings, ...filteredSettings }
          : {
              ...user.mapSettings,
              ...filteredSettings,
              [MAP_SETTINGS.general.SHOW_JOB_CLUSTERS]: true,
            },
    });
  }, [handleSetUserParam, selectedType, user.mapSettings]);

  const types = Object.keys(OPTIONS).map((key) => ({ key: OPTIONS[key], value: key }));

  useEffect(() => {
    setShowPanel(user.openPanel === PANELS.MAP_SETTINGS);
  }, [user.openPanel, setShowPanel]);

  useEffect(() => {
    if (!showProblem) setSelectedType(OPTIONS.Solution);
    else setSelectedType(OPTIONS.General);
  }, [showProblem]);

  useEffect(() => {
    if (selectedType === OPTIONS.Solution && scrollableContentRef.current) {
      scrollableContentRef.current.scrollTop = 0;
    }
  }, [selectedType]);

  return (
    <>
      <Modal isDark isVisible={showPanel} isFull fromTop>
        <StyledContrastPanel fullDisplay data-test-id="mapSettingsPanel" column>
          <StyledContrastPanelHeader size="0">
            <StyledText bold contrast>
              {mapSettingsPanel.mapSettingsTitle}
            </StyledText>
          </StyledContrastPanelHeader>
          <StyledScrollableContent ref={scrollableContentRef}>
            <Section extraPaddingTop>
              <Selector
                id="button-type-select"
                initialValue={selectedType}
                options={types}
                inverted
                onChange={setSelectedType}
                disabledTitle={mapSettingsPanel.solutionOptionDisabled}
              />
            </Section>
            <Section>
              <StyledContrastPanelColumnsContainer fullModalHeight adjusted>
                {settings.map((setting) =>
                  setting.value === MAP_SETTINGS.solution.SHOW_TOUR_CONCAVE_HULL ? (
                    <MultipleActionSetting
                      key={setting.value}
                      mapSettingsPanel={mapSettingsPanel}
                      mapSettings={user.mapSettings}
                      setting={setting}
                      handleOnSwitchChange={handleOnSwitchChange}
                      handleOnChangeInput={handleOnChangeInput}
                    />
                  ) : (
                    <SingleActionSetting
                      key={setting.value}
                      mapSettings={user.mapSettings}
                      mapSettingsPanel={mapSettingsPanel}
                      setting={setting}
                      handleOnSwitchChange={handleOnSwitchChange}
                    />
                  ),
                )}
              </StyledContrastPanelColumnsContainer>
            </Section>
          </StyledScrollableContent>
          <StyledContrastPanelFooterSettings isDouble isFullWidth mapSettings removeAbsolute>
            <Button
              text={mapSettingsPanel.resetDefaultSettings}
              inverted
              contrast
              onClick={onResetDefault}
              dataTestId="mapSettingsResetBtn"
            />
            <div />
            <Button
              text={solverConfigLabels.buttonDoneLabel}
              onClick={handleDoneClick}
              dataTestId="mapSettingsDoneBtn"
            />
          </StyledContrastPanelFooterSettings>
        </StyledContrastPanel>
      </Modal>
      <StyledAccessContainer
        data-testid="mapSettingsBtn"
        id="map-settings-entry-point"
        onClick={() => togglePanel()}
        order={1}
        icon={mapSettings}
        title={mapSettingsPanel.mapSettingsTitle}
        wizardShown={wizardShown}
      />
    </>
  );
};

export default withTranslation(MapSettingsPanel);
